const objectHash = require('object-hash');
const { apiResponseToError } = require('@/lib/Api');

/**
 * @constructor
 *
 * Handles sending the cart off to an external source (the DT API) to perform some calculations
 * we don't do on ProPoint.
 * Initially this was just for offers but now it's also for calculating price differences when amending orders.
 *
 * @param $timeout
 * @param cartCalculationResource
 * @param {CartToReservationStructureMapper} cartToReservationStructureMapper
 * @param {ConnectivityChecker} connectivityChecker
 * @param {OfferApplicator} offerApplicator
 * @param {PaymentService} paymentService
 */
const ExternalCartCalculator = function (
    $timeout,
    cartCalculationResource,
    cartToReservationStructureMapper,
    connectivityChecker,
    offerApplicator,
    paymentService
) {
    this.$timeout = $timeout;
    this.cartCalculationResource = cartCalculationResource;
    this.cartToReservationStructureMapper = cartToReservationStructureMapper;
    this.connectivityChecker = connectivityChecker;
    this.offerApplicator = offerApplicator;
    this.paymentService = paymentService;

    /**
     * @type {?string}
     */
    this.lastRequestHash = null;
};

ExternalCartCalculator.prototype = {
    /**
     * @param {CartService} cartService
     * @param {Payment[]} [proposedPayments]
     * @param {boolean} [force]
     *
     * @returns {Promise}
     */
    recalculate(cartService, proposedPayments = [], force = false) {
        let requestStructure = this.buildStructureForCartCalculationsRequest(cartService, proposedPayments);

        if (!requestStructure.lines || Object.values(requestStructure.lines).length < 1) {
            // No lines in the request - no need to send.
            // Have cart recalculate itself so manual offers are updated.
            cartService.calculate(false);

            return Promise.resolve({});
        }

        // Wrapped in a timeout to trigger screen update faster in Angular.
        this.$timeout(() => {
            cartService.externalCalculationInProgress = true;
        });

        return this.sendRequest(requestStructure, force)
            .then((response) => {
                // Clear all existing discounts.
                cartService.clearDiscounts();

                // Add the auto offers to the cart.
                return this.offerApplicator.applyOffersToCart(cartService, response.discounts)
                    .then(() => {
                        return response;
                    });
            })
            .catch((error) => {
                console.warn('Cart external recalculation failed', error);

                // Re-throw the error so other catch's in the chain can run.
                throw error;
            })
            .finally(() => {
                cartService.calculate(false);

                this.$timeout(() => {
                    cartService.externalCalculationInProgress = false;
                });
            });
    },

    /**
     * @param {object} requestStructure
     * @param {boolean} [force] Make the request even if it's a duplicate.
     *
     * @return {Promise<any>}
     */
    sendRequest(requestStructure, force) {
        return new Promise((resolve, reject) => {
            try {
                if (!this.connectivityChecker.isOnline()) {
                    reject(new Error('Offline'));
                    return;
                }

                let hash = this.generateHashForRequestStructure(requestStructure);

                if (!force && hash === this.lastRequestHash) {
                    reject(new Error('Ignoring duplicate hash'));
                    return;
                }

                // Add the hash to the structure so the API returns it.
                requestStructure.hash = hash;

                // Remember the last hash sent so we can discard outdated responses.
                // This to prevent a race condition where an earlier request comes back after
                // the latest one, giving us old data.
                this.lastRequestHash = hash;

                this.cartCalculationResource.calculate(
                    requestStructure,
                    (response) => {
                        // Only use this response if the hash is the latest one we are expecting.
                        if (response.hash !== this.lastRequestHash) {
                            reject(new Error(`Response for old request received (${response.hash})`));
                        } else {
                            // The API can return both errors and an array of 0 or more discounts.
                            // Therefore, we need to update the cart based on the applicable discounts returned from
                            // the API and also display any errors in a warning Toast.
                            resolve(response);
                        }
                    },
                    (err) => reject(apiResponseToError(err))
                );
            } catch (error) {
                reject(error);
            }
        });
    },

    /**
     * @param {CartService} cartService
     * @param {Payment[]} proposedPayments
     *
     * @return {object}
     */
    buildStructureForCartCalculationsRequest(cartService, proposedPayments) {
        let reservationStructure = this.cartToReservationStructureMapper.removeZeroQtyLines(
            this.cartToReservationStructureMapper.map(cartService.currentCart, false)
        );

        const { manualOfferIDs, couponCodeOfferCodes } = this.buildManualOffersArray(
            cartService.currentCart.selectedManualOfferIDs,
            cartService.currentCart.selectedCouponCodeOfferCodes,
        );

        reservationStructure.manualOfferIDs = manualOfferIDs;
        reservationStructure.offersCodes = couponCodeOfferCodes;

        // Add any 'proposed' payments such as experience gift vouchers.
        // The "cart calculations" endpoint uses these to adjust pricing of items and to check whether
        // the vouchers can actually be applied to the current cart
        reservationStructure.payments = this.paymentService.convertPaymentsToApiStructure(
            proposedPayments.filter((p) => !p.ID)
        );

        Object.keys(reservationStructure.lines).forEach((key) => {
            // Remove strange items that shouldn't be discounted.
            // FIXME: Should this be done by the API instead?
            let line = reservationStructure.lines[key];
            if (line.itemType === 'Order Balance') {
                delete reservationStructure.lines[key];
            }
        });

        return reservationStructure;
    },

    /**
     * Convert an object from the format manual offerIDs are stored in the cart:
     * {offerID: numberOfTimesApplies, 1: 3}
     * to the format required by the API, which is an array with each offerID appearing in it the number of times
     * it can be applied:
     * [1, 1, 1]
     *
     * @param {Object<number, number>} selectedManualOfferIDs
     * @param {Object<number, string>} selectedCouponCodeOfferCodes
     *
     * @return {{couponCodeOfferCodes: number[], manualOfferIDs: {Object<string, string>}[]}}
     */
    buildManualOffersArray(selectedManualOfferIDs, selectedCouponCodeOfferCodes) {
        // An array of offerIDs that have been unlocked by the operator - the same ID can appear multiple times.
        let manualOfferIDs = [];

        // An array of objects with the structure { code: {String} }.
        let couponCodeOfferCodes = [];

        // An array of offerIDs corresponding to the couponCodeOfferCodes codes.
        let couponCodeOfferIDs = [];

        // Process the codes first, so we can keep a record of the offerIDs and exclude them from
        // the array of non coupon code offer IDs.
        Object.keys(selectedCouponCodeOfferCodes).forEach((offerID) => {
            couponCodeOfferCodes.push({ code: selectedCouponCodeOfferCodes[offerID] });
            couponCodeOfferIDs.push(offerID);
        });

        // Convert from the object of offer IDs with a count to the array with repeated values.
        Object.keys(selectedManualOfferIDs).forEach((offerID) => {
            if (!couponCodeOfferIDs.includes(offerID)) {
                offerID = parseInt(offerID, 10);
                let count = selectedManualOfferIDs[offerID];
                for (let i = 0; i < count; i++) {
                    manualOfferIDs.push(offerID);
                }
            }
        });

        return { manualOfferIDs, couponCodeOfferCodes };
    },

    /**
     * @param {object} requestStructure
     *
     * @return {string}
     */
    generateHashForRequestStructure(requestStructure) {
        return objectHash(requestStructure);
    },
};

module.exports = ExternalCartCalculator;
